import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { useRouting, useScrollTop } from '@alycecom/hooks';

import PageWithDataWrapper from '../../../../components/PageWithDataWrapper';
import StyledHeader from '../StyledHeader/StyledHeader';
import Footer from '../../../../components/Shared/Footer';
import GiftInfoCard from '../GiftInfoCard/GiftInfoCard';
import { getGiftGl, getGiftHashId, getGiftProduct } from '../../store/data/data.selectors';
import { getMessage, getIsLoading, getIsAnswerSent } from '../../store/finishInfo/finishInfo.selectors';
import { finishInfoRequest, finishInfoSendRequest } from '../../store/finishInfo/finishInfo.actions';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import { getStatuses } from '../../store/statuses/statuses.selectors';
import { getIsGiftDataLoaded } from '../../store/recipient.selectors';
import { getSenderState } from '../../../../store/users/users.selectors';

import { NeedMoreInfoTitle, NeedMoreInfoForm, ConciergeInfo, NeedMoreInfoMessage } from './NeedMoreInfoContent';

const NeedMoreInfoPage = (): JSX.Element => {
  const go = useRouting();
  const dispatch = useDispatch();

  const isGiftDataLoaded = useSelector(getIsGiftDataLoaded);
  const hashId = useSelector(getGiftHashId);
  const gl = useSelector(getGiftGl);
  const sender = useSelector(getSenderState);
  const product = useSelector(getGiftProduct);

  const isLoading = useSelector(getIsLoading);
  const message = useSelector(getMessage);
  const isAnswerSent = useSelector(getIsAnswerSent);

  const statusFlags = useSelector(getStatuses);
  const wrapperMarginTop = statusFlags.preview ? [10, 5] : [];

  const handleSendMessage = useCallback(
    sendMessage => {
      if (!statusFlags.preview) {
        dispatch(finishInfoSendRequest({ hashId, message: sendMessage, product }));
      }
    },
    [dispatch, hashId, statusFlags, product],
  );

  useScrollTop();

  usePageLoadEvent('Need More Info Page - Opened');

  useEffect(() => {
    if (hashId) {
      dispatch(finishInfoRequest({ hashId }));
    }
  }, [dispatch, hashId]);

  useEffect(() => {
    if (isAnswerSent) {
      go(`/${hashId}/thank-you`);
    }
  }, [go, hashId, isAnswerSent]);

  return (
    <PageWithDataWrapper isLoaded={isGiftDataLoaded}>
      <Box bgcolor="common.white" mt={wrapperMarginTop}>
        <StyledHeader />
        <Box
          width={1}
          minHeight={['calc(100vh - 56px)', 'calc(100vh - 80px)']}
          className="need-more-info-ie"
          display="flex"
          flexDirection="column"
        >
          <NeedMoreInfoTitle />
          <Box width={[320, 430]} mx="auto">
            <Box mb={3}>
              <ConciergeInfo>
                <NeedMoreInfoMessage message={message} isLoading={isLoading} />
              </ConciergeInfo>
            </Box>
            <GiftInfoCard product={product} sender={sender} gl={gl} minHeight={[112, 112, 160]} />
          </Box>
          <NeedMoreInfoForm onSubmit={handleSendMessage} />
          <Footer mobileHidden />
        </Box>
      </Box>
    </PageWithDataWrapper>
  );
};

export default NeedMoreInfoPage;
